import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { GuestRoute } from "./GuestRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import LoginPage from "@/pages/auth/LoginPage";
import CreateProfilePage from "@/pages/create-profile/CreateProfilePage";
import NotFoundPage from "@/pages/NotFoundPage";
import LandingLayout from "@/components/layout/LandingPageLayout";
import DiscoverPage from "@/pages/discover/DiscoverPage";
import SettingsPage from "@/pages/mysupport/SettingsPage";
import SignupPage from "@/pages/signup/SignupPage";
import SignupDocumentsPage from "@/pages/signup/SignupDocumentsPage";
import AnonymousProfile from "@/components/shared/AnonymousCompanyProfile/AnonymousProfile";
import RegistrationSuccessPage from "@/pages/signup/RegistrationSuccessPage";
import ForgotPassword from "@/pages/forgot-password/ForgotPassword";
import ResendEmail from "@/pages/resend-email/ResendEmail";
import MyProfilePage from "@/pages/profile/MyProfilePage";
import InterestsPage from "@/pages/interests/InterestsPage";
import CollaborationsPage from "@/pages/collaborations/CollaborationsPage";
import ShortlistPage from "@/pages/shortlist/ShortlistPage";
import InvoicesPage from "@/pages/invoices/InvoicesPage";
import WalletPage from "@/pages/invoices/WalletPage";
import ResetPassword from "@/pages/reset-password/ResetPassword";
import ActivateAccountPage from "@/pages/activate-account/ActivateAccountPage";
import EditProfile from "@/pages/edit-profile/EditProfile";
import Maintenance from "@/pages/maintenance/Maintenance";
import MatchSuccessPage from "@/pages/interests/components/ItsaMatchPage";
import PaymentSuccessPage from "@/pages/interests/components/PaymentSuccessPage";
import CollaborationWorkspacePage from "@/pages/collaborations/CollaborationWorkspacePage";
import CollaborationCompanyWorkspacePage from "@/pages/collaborations/CollaborationCompanyWorkspacePage";
import LoggedInCompanyWorkspaceRedirect from "@/routes/LoggedInCompanyWorkspaceRedirect";
import { DiscoverCompanyProfileGate } from "@/routes/DiscoverCompanyProfileGate";
import MatchedProfileView from "@/pages/profile/MatchedView";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import { AdminProtectedRoute } from "./AdminProtectedRoute";
import { AdminLayout } from "@/components/layout/AdminLayout";
import AdminDashboardPage from "@/pages/admin/company-lists/AdminDashboardPage";
import AdminPendingVerificationPage from "@/pages/admin/company-lists/AdminPendingVerificationPage";
import AdminCollaborationTable from "@/pages/admin/collaboration-lists/AdminCollaborationTable";
import AdminDiscoverTable from "@/pages/admin/discover-listings/AdminDiscoverTable";
import AdminGuidancePage from "@/pages/admin/guidance-table/AdminGuidancePage";
import AdminCompanyDetailView from "@/pages/admin/company-lists/components/AdminMatchedView";
import AdminCollaborationDetailPage from "@/pages/admin/collaboration-lists/components/WorkspaceView";
import AdminInterestManagement from "@/pages/admin/interest-management/InterestManagement";
import AdminExpressInterestPreviewPage from "@/pages/admin/express-interest/AdminExpressInterestPreviewPage";
import AdminUsersPage from "@/pages/admin/users/AdminUsersPage";
import AdminCreditAdjustmentHistoryPage from "@/pages/admin/users/AdminCreditAdjustmentHistoryPage";
import AdminCompanyOnboardingChartPage from "@/pages/admin/company-lists/AdminCompanyOnboardingChartPage";
import AdminCountriesMapPage from "@/pages/admin/company-lists/AdminCountriesMapPage";
import AdminIndirectVerificationPage from "@/pages/admin/indirect-verification/AdminIndirectVerificationPage";
import AdminBulkMailPage from "@/pages/admin/bulk-mail/AdminBulkMailPage";
import AdminBulkMailHistoryPage from "@/pages/admin/bulk-mail/AdminBulkMailHistoryPage";
import AdminMeilisearchReindexPage from "@/pages/admin/meilisearch/AdminMeilisearchReindexPage";
import AdminOuterUsersPage from "@/pages/admin/outer-users/AdminOuterUsersPage";
import AdminCompanyFullEditPage from "@/pages/admin/company-lists/AdminCompanyFullEditPage";
import CreditsPage from "@/pages/credits/CreditsPage";
import BuyCreditsPage from "@/pages/credits/components/BuyCreditsPage";
import LoginTwoFactorPage from "@/two-factor/pages/LoginTwoFactorPage";
import EnableTwoFactorPage from "@/two-factor/pages/EnableTwoFactorPage";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import SectorDiscoverPage from "@/pages/home/SectorDiscoverPage";

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route element={<GuestRoute />}>
        <Route element={<LandingLayout />}>
          <Route path="/" element={<DiscoverPage />} />
          <Route path="/sector/:slug" element={<SectorDiscoverPage />} />
          <Route path="/discover" element={<DiscoverPage />} />
        </Route>
      </Route>
      {/* Admin/shared discover company URL — must work for guests and logged-in clients */}
      <Route path="/discover/:id" element={<DiscoverCompanyProfileGate />}>
        <Route element={<LandingLayout />}>
          <Route index element={<AnonymousProfile pageID="DISCOVER_PUBLIC" />} />
        </Route>
      </Route>
      <Route path="/registration-success" element={<RegistrationSuccessPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/2fa" element={<LoginTwoFactorPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup/company-details" element={<Navigate to="/login" replace />} />
      <Route path="/signup/documents" element={<SignupDocumentsPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/resend-email" element={<ResendEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/activate-account" element={<ActivateAccountPage />} />

      {/* Protected routes with AppShell layout */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/create-profile" element={<CreateProfilePage />} />
        <Route path="/profile" element={<MyProfilePage />} />
        <Route path="/my-discovery" element={<DiscoverPage />} />
        <Route path="/my-discovery/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
        <Route path="/requests" element={<InterestsPage />} />
        <Route path="/collaborations" element={<CollaborationsPage />} />
        <Route path="/shortlist" element={<ShortlistPage />} />
        <Route path="/shortlist/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="/credits/buy" element={<BuyCreditsPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/wallet" element={<WalletPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/two-factor/setup" element={<EnableTwoFactorPage />} />
        <Route path="/profile/edit" element={<EditProfile />} />
        <Route path="/requests/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
        <Route path="/requests/match-success/:id" element={<MatchSuccessPage />} />
        <Route path="/requests/payment-success/:id" element={<PaymentSuccessPage />} />

        <Route path="/collaborations/company/:companyId" element={<CollaborationCompanyWorkspacePage />} />
        <Route path="/collaborations/:id" element={<CollaborationWorkspacePage />} />
        <Route path="/collaborations/:id/profile" element={<MatchedProfileView />} />
      </Route>

      {/* ============ Admin routes ============ */}
      <Route path="/admin/login" element={<AdminLoginPage />} />

      <Route
        element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        }
      >
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/outer-users" element={<AdminOuterUsersPage />} />
        <Route path="/admin/credit-history" element={<AdminCreditAdjustmentHistoryPage />} />
        <Route path="/admin/bulk-mail/history" element={<AdminBulkMailHistoryPage />} />
        <Route path="/admin/bulk-mail" element={<AdminBulkMailPage />} />
        <Route path="/admin/company-onboarding" element={<AdminCompanyOnboardingChartPage />} />
        <Route path="/admin/countries-map" element={<AdminCountriesMapPage />} />
        <Route path="/admin/companies" element={<AdminDashboardPage />} />
        <Route path="/admin/pending-verification" element={<AdminPendingVerificationPage />} />
        <Route path="/admin/indirect-verification" element={<AdminIndirectVerificationPage />} />
        <Route path="/admin/meilisearch" element={<AdminMeilisearchReindexPage />} />
        <Route path="/admin/collaborations" element={<AdminCollaborationTable />} />
        <Route path="/admin/interest" element={<AdminInterestManagement />} />
        <Route path="/admin/express-interest-preview" element={<AdminExpressInterestPreviewPage />} />
        <Route path="/admin/discover" element={<AdminDiscoverTable />} />
        <Route path="/admin/resource" element={<AdminGuidancePage />} />
        <Route path="/admin/companies/:id" element={<AdminCompanyDetailView />} />
        <Route path="/admin/companies/:id/edit" element={<AdminCompanyFullEditPage />} />
        <Route path="/admin/collaborations/:id" element={<AdminCollaborationDetailPage />} />
        <Route path="/admin/two-factor" element={<EnableTwoFactorPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
      <Route path="/maintenance" element={<Maintenance />} />
    </Routes>
  );
}

export default AppRoutes;
